<template>
  <div class="container page-area">
    <q-card v-show="!isLoading" class="page-area__card">
      <q-card-section class="page-area__card-section">
        <q-tabs
          v-model="currentTab"
          align="left"
          class="q-mb-lg page-area__top"
        >
          <q-tab name="zoneForm" label="Зона" />
          <q-tab name="intervalsForm" label="Интервалы" />
        </q-tabs>

        <div class="page-area__content">
          <q-form
            v-show="currentTab === 'zoneForm'"
            ref="zoneForm"
            greedy
          >
            <YMapArea
              :area="currentArea"
              :other-areas="areas"
              :center="shop.coordinates"
              :placemark="{
                hintContent: shop.address,
                balloonContent: `${[shop.city, shop.street, shop.house].join(', ')}`,
                coordinates: shop.coordinates
              }"
              @change="onYMapChange"
            />
          </q-form>

          <q-form
            v-show="currentTab === 'intervalsForm'"
            ref="intervalsForm"
            greedy
          >
            <Intervals
              :intervals="intervals"
              :min="1"
              @add="addInterval"
              @change="updateInterval"
              @remove="removeInterval"
            />
          </q-form>
        </div>

        <div class="text-right page-area__bottom">
          <q-btn label="Сохранить" @click="submit" />
        </div>
      </q-card-section>
    </q-card>
    <q-inner-loading :showing="isLoading" />
  </div>
</template>

<script>
/**
 * Корневой Vue компонент модуля [PageArea]{@link module:PageArea}
 * @module PageArea/Component/App
 * @requires PageArea
 * @requires PageArea/Component/Intervals
 * @requires PageArea/Store
 * @requires npm:lodash
 * @requires npm:vuex
 * @requires Shared/Component/YMapArea
 * @requires Utils/notify
 * @requires Constants/successMessages
 * @listens module:Shared/Component/YMapArea#change
 * @listens module:PageArea/Component/Intervals#add
 * @listens module:PageArea/Component/Intervals#change
 * @listens module:PageArea/Component/Intervals#remove
 * @fires module:PageArea/Component/App#submit
 * @vue-data {('zoneForm'|'intervalsForm')} currentTab=zoneForm - Текущая вкладка
 * @vue-data {Array.<Object>} intervals - Интервалы
 * @vue-data {Object} currentArea - Зона доставки
 * @vue-data {Array.<Object>} areas - Другие зоны доставки магазина
 * @vue-data {Array.<String>} forms - Имена форм для перебора и валидации
 * @vue-data {Boolean} isLoading=false - Идет загрузка данных
 * @vue-data {Object} queryParams - Параметры URL
 * @vue-event {Object} submit - Зона успешно сохранена
 * */

import Intervals from 'PageArea/components/Intervals'
import store from 'PageArea/store'
import { cloneDeep } from 'lodash'
import { mapActions, mapGetters, mapState } from 'vuex'
import YMapArea from 'Shared/YMapArea'
import { notifyError, notifySuccess } from 'Utils/notify'
import { SAVED } from 'Constants/successMessages'

/**
 * Стандартное состояние компонента
 * @type {Object}
 * */
const defaultState = () => ({
  /**
   * Текущая вкладка
   * @type {('zoneForm'|'intervalsForm')}
   * @default 'zoneForm'
   * */
  currentTab: 'zoneForm',
  /**
   * Интервалы
   * @type {Array.<Object>}
   * @default []
   * */
  intervals: [],
  /**
   * Зона доставки
   * @type {Object}
   * */
  currentArea: {
    name: null,
    description: null,
    shapes: []
  },
  /**
   * Другие зоны доставки магазина
   * @type {Array.<Object>}
   * @default []
   * */
  areas: [],
  /**
   * Имена форм для перебора и валидации
   * @type {Array.<String>}
   * */
  forms: ['zoneForm', 'intervalsForm'],
  /**
   * Идет загрузка данных
   * @type {Boolean}
   * @default false
   * */
  isLoading: false
})

export default {
  store,
  name: 'PageArea',
  components: {
    Intervals,
    YMapArea
  },
  props:{
    shopId: String,
    isCreateMode: Boolean,
    areaId: String
  },
  data: () => ({
    /**
     * Параметры URL
     * Обновляется из модуля
     * @type {Object}
     * */
    queryParams: {
      name: null,
      id: null
    },
    ...defaultState()
  }),
  computed: {
    ...mapGetters(['getEmptyInterval']),
    ...mapState(['shop'])
  },
  created() {
      this.init()
  },
  beforeDestroy() {
    this.clear()
  },
  methods: {
    ...mapActions([
      'getAreaOptionsById',
      'clearStore',
      'getShopAreas',
      'create',
      'update'
    ]),

    /**
     * Инициализация компонента и карты
     * @async
     * @returns {Promise.<void>}
     * */
    async init() {
      this.isLoading = true

      try {
        if (!this.shopId) {
          throw new Error('Идентификатор магазина обязателен')
        }

        if (!this.isCreateMode) {
          if (!this.areaId) throw new Error('Идентификатор зоны обязателен')
          await this.getAreaOptionsById(this.areaId)
        }

        this.$set(this, 'intervals', cloneDeep(this.$store.state.intervals))
        this.$set(this, 'currentArea', cloneDeep(this.$store.state.currentArea))
        this.$set(this, 'areas', cloneDeep(this.$store.state.areas))
      } catch (e) {
        console.error(e)
        notifyError(e.message)
        if (!this.isEmbed) {
          await this.$router.push('/not-found')
        }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Сохранение зоны и опций доставки (сум, интервалов)
     * @async
     * @returns {Promise.<void>}
     * @fires module:PageArea/Component/App#submit
     * */
    async submit() {
      const formsIsValid = await Promise.all(
        this.forms.map(name => this.$refs[name].validate()
          .then(isValid => ({
            name,
            isValid
          })))
      )

      const currentForm = formsIsValid.find(form => form.name === this.currentTab)
      if (!currentForm.isValid) return

      const invalidForm = formsIsValid.find(form => !form.isValid)

      if (invalidForm) {
        this.currentTab = invalidForm.name
        return
      }

      const resultObject = {
        intervals: this.intervals,
        area: this.currentArea
      }

      if (this.isCreateMode) {
        await this.create(resultObject)
        this.$set(this, 'currentArea', cloneDeep(this.$store.state.currentArea))
      } else {
        await this.update(resultObject)
      }

      notifySuccess(SAVED)

      if (this.isEmbed) {
        /**
           * Зона успешно сохранена
           * @event module:PageArea/Component/App#submit
           * @type {Object}
           * @property {Array.<Object>} intervals
           * @property {Object} area
           * */
        this.$emit('submit', cloneDeep(resultObject))
      }
    },

    /**
     * Добавление интервала в локальное состояние
     * @listens module:PageArea/Component/Intervals#add
     * */
    addInterval() {
      this.intervals.push(this.getEmptyInterval())
    },

    /**
     * Обновленеие интервала в локальном состоянии
     * @listens module:PageArea/Component/Intervals#change
     * */
    updateInterval({ index, value }) {
      this.intervals.splice(index, 1, value)
    },

    /**
     * Удалить интервал из локального состояния
     * @listens module:PageArea/Component/Intervals#remove
     * */
    removeInterval(index) {
      this.intervals.splice(index, 1)
    },

    /**
     * Обновить зону доставки в локальном состоянии
     * @listens module:Shared/Component/YMapArea#change
     * */
    onYMapChange(area) {
      Object.entries(area)
        .forEach(([key, value]) => {
          this.$set(this.currentArea, key, value)
        })
    },

    /**
     * Сбрость локальное состояние, хранилище, валидацию
     * */
    clear() {
      Object.entries(defaultState())
        .forEach(([key, value]) => {
          this.$set(this, key, value)
        })
      this.forms.forEach(formName => this.$refs[formName].resetValidation())
      this.clearStore()
    }
  }
}
</script>

<style lang="scss">
.page-area {
  display: flex;
  flex-direction: column;
  min-height: 100%;

  .page-area__card,
  .page-area__card-section,
  .page-area__content,
  .page-area__content .q-form {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  .page-area__content .q-form > * {
    flex-grow: 1;
  }
}
</style>
